/**
 * SectionBookingRules — las condiciones con las que se acepta una reserva.
 *
 * Cuatro decisiones distintas conviven aquí y se separan a propósito, porque
 * el cliente las piensa por separado: cuánto puede durar una estancia, qué
 * pasa entre que alguien reserva y confirma, quién decide la villa concreta y
 * qué puede reservar el propietario para su propio uso.
 *
 * Dos criterios de diseño:
 *
 *   - Los valores marcados con "Aprobado" salen de `BOOKING_RULES_APPROVED_FIELDS`
 *     y no de una lista escrita a mano: si mañana cambia el dataset, cambia la
 *     marca. Todo lo demás es ejemplo de la vista previa y no debe leerse como
 *     producto decidido.
 *   - El reparto automático aparece aquí solo como interruptor. Los grupos y
 *     los pesos viven en Alojamientos y no se duplican: repetirlos invitaría a
 *     configurarlos dos veces y a que dejaran de coincidir.
 *
 * Como el resto de la vista previa, no hay red: todo sale de `settingsV2Store`.
 */
import React from "react";
import { ArrowRight, CalendarBlank, Globe, Scales } from "@phosphor-icons/react";
import {
  Block, Card, FieldGrid, Field, Select, Toggle, Note, Advanced, ReadRow,
  ApprovedMark, StatusChip,
} from "@/pages/admin/pms/settings/SettingsPrimitives";
import {
  useSettingsV2, updateSection, updateAllocation, unitLabelFor,
} from "@/lib/settingsV2Store";
import { CANCELLATION_POLICIES, BOOKING_RULES_APPROVED_FIELDS } from "@/data/settingsV2Demo";

/** La marca "Aprobado" la decide el dataset, no esta pantalla. */
const isApproved = (key) => BOOKING_RULES_APPROVED_FIELDS.includes(key);

const nights = (n) => `${n} ${Number(n) === 1 ? "noche" : "noches"}`;
const days = (n) => `${n} ${Number(n) === 1 ? "día" : "días"}`;

export default function SectionBookingRules() {
  const { booking, property, website, allocation } = useSettingsV2();
  const set = (patch) => updateSection("booking", patch);
  const label = unitLabelFor(property);
  const one = label.singular.toLowerCase();
  const many = label.plural.toLowerCase();

  const blackout = booking.owner_stay_blackout || [];

  return (
    <div data-testid="section-booking">
      <Note testId="booking-approved-note">
        Los valores marcados como «Aprobado» vienen de decisiones de producto ya tomadas y se
        muestran tal cual. El resto son ejemplos de la vista previa para poder juzgar la pantalla:
        no fijan todavía ninguna regla.
      </Note>

      {/* -- Estancia ------------------------------------------------------ */}
      <Block
        title="Estancia"
        description="Cuánto puede durar una reserva y con cuánta antelación tiene que llegar."
        testId="booking-stay"
      >
        <Card testId="booking-stay-card">
          <FieldGrid>
            <Field
              label="Estancia mínima"
              type="number"
              value={booking.min_stay_nights}
              onChange={(v) => set({ min_stay_nights: v })}
              suffix="noches"
              hint="Lo más corto que aceptas"
              testId="booking-min-stay"
            />
            <Field
              label="Estancia máxima"
              type="number"
              value={booking.max_stay_nights}
              onChange={(v) => set({ max_stay_nights: v })}
              suffix="noches"
              hint="Lo más largo que aceptas"
              testId="booking-max-stay"
            />
            <Field
              label="Antelación mínima"
              type="number"
              value={booking.lead_time_days}
              onChange={(v) => set({ lead_time_days: v })}
              suffix="días"
              hint="Días entre la reserva y la llegada"
              testId="booking-lead-time"
            />
            <Field
              label="Hora de cierre del día"
              type="time"
              value={booking.cutoff_hour}
              onChange={(v) => set({ cutoff_hour: v })}
              hint="A partir de esa hora ya no entran llegadas para hoy"
              testId="booking-cutoff"
            />
          </FieldGrid>

          <p
            className="mt-5 pt-4 border-t border-stone-100 dark:border-stone-800 text-sm text-stone-600 dark:text-stone-300"
            data-testid="booking-stay-summary"
          >
            Con estos valores, una reserva puede durar entre{" "}
            <span className="text-stone-900 dark:text-stone-100">{nights(booking.min_stay_nights)}</span>{" "}
            y{" "}
            <span className="text-stone-900 dark:text-stone-100">{nights(booking.max_stay_nights)}</span>,
            hay que reservarla con{" "}
            <span className="text-stone-900 dark:text-stone-100">{days(booking.lead_time_days)}</span>{" "}
            de antelación y lo que llegue después de las{" "}
            <span className="text-stone-900 dark:text-stone-100">{booking.cutoff_hour}</span>{" "}
            ya no cuenta para hoy.
          </p>
        </Card>
      </Block>

      {/* -- Reserva ------------------------------------------------------- */}
      <Block
        title="Reserva"
        description="Qué ocurre entre que alguien aparta unas fechas y confirma, y en qué condiciones puede cancelar."
        testId="booking-reservation"
      >
        <Card testId="booking-reservation-card">
          <FieldGrid>
            <Field
              label="Tiempo para confirmar"
              type="number"
              value={booking.hold_duration_hours}
              onChange={(v) => set({ hold_duration_hours: v })}
              suffix="horas"
              hint="Las fechas quedan apartadas durante ese tiempo"
              testId="booking-hold-duration"
            />
            <Select
              label="Política de cancelación"
              value={booking.cancellation_policy}
              onChange={(v) => set({ cancellation_policy: v })}
              options={CANCELLATION_POLICIES}
              hint="La que se enseña al huésped al reservar"
              testId="booking-cancellation-policy"
            />
          </FieldGrid>

          <Advanced label="Qué pasa cuando pasa ese tiempo" testId="booking-hold-advanced">
            <p className="text-sm text-stone-600 dark:text-stone-300 max-w-2xl">
              Apartar fechas no es confirmarlas. Mientras dura ese margen, la{" "}
              {one} no se ofrece a nadie más; si nadie confirma antes de que termine, las fechas
              vuelven a estar libres y la reserva queda sin efecto. La política de cancelación, en
              cambio, se aplica solo a las reservas ya confirmadas.
            </p>
          </Advanced>

          <div className="mt-5 pt-4 border-t border-stone-100 dark:border-stone-800">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Globe size={14} className="text-stone-400" />
              <StatusChip tone="stone" testId="booking-direct-status">
                Pendiente de publicar la web
              </StatusChip>
            </div>
            <Toggle
              label="Aceptar reservas directas desde la web"
              description="Reservas que entran por tu propia web, sin comisión de portal."
              checked={website.direct_booking_enabled}
              onChange={(v) => updateSection("website", { direct_booking_enabled: v })}
              testId="booking-direct-enabled"
            />
            <Note testId="booking-direct-note">
              Es el mismo ajuste que aparece en Web: se configura una vez y se ve desde los dos
              sitios. Solo empieza a funcionar cuando la web esté publicada con dominio propio y
              haya forma de cobrar, y hoy falta lo uno y lo otro.
            </Note>
          </div>
        </Card>
      </Block>

      {/* -- Distribución automática --------------------------------------- */}
      <Block
        title="Distribución automática"
        description="Quién decide qué alojamiento concreto recibe cada reserva."
        testId="booking-allocation"
      >
        <Card testId="booking-allocation-card">
          <Toggle
            label={`Repartir las reservas automáticamente entre ${many} equivalentes`}
            description={`Cuando entra una reserva, el sistema elige qué ${one} la recibe para que el trabajo y los ingresos queden repartidos. Si lo apagas, cada reserva se asigna a mano.`}
            checked={allocation.enabled}
            onChange={(v) => updateAllocation({ enabled: v })}
            testId="booking-auto-allocation"
          />

          <div
            className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-800 flex items-start gap-2"
            data-testid="booking-allocation-pointer"
          >
            <Scales size={14} className="mt-0.5 shrink-0 text-stone-400" />
            <p className="text-xs text-stone-500 dark:text-stone-400 max-w-2xl">
              Qué {many} compiten entre sí y con qué criterios se reparten se configura una sola
              vez en Alojamientos
              <ArrowRight size={11} className="inline mx-1 align-[-1px] text-stone-400" />
              Distribución automática. Aquí solo se enciende o se apaga.
            </p>
          </div>
        </Card>
      </Block>

      {/* -- Estancias del propietario ------------------------------------- */}
      <Block
        title="Estancias del propietario"
        description={`Las noches que el propietario de cada ${one} puede reservar para su propio uso.`}
        testId="booking-owner-stays"
      >
        <Card testId="booking-owner-stays-card">
          <Toggle
            label="Permitir estancias del propietario"
            description={`Ocupan la ${one} igual que una reserva, pero no se venden ni cuentan como ingreso.`}
            checked={booking.owner_stays_enabled}
            onChange={(v) => set({ owner_stays_enabled: v })}
            testId="booking-owner-stays-enabled"
          />

          <div className="mt-5 pt-4 border-t border-stone-100 dark:border-stone-800">
            <FieldGrid>
              <Field
                label="Cuota anual"
                type="number"
                value={booking.owner_stay_quota_nights}
                onChange={(v) => set({ owner_stay_quota_nights: v })}
                suffix="noches"
                hint={`Por propietario y año, para su ${one}`}
                approved={{ baseline: 60, value: booking.owner_stay_quota_nights }}
                disabled={!booking.owner_stays_enabled}
                testId="booking-owner-quota"
              />
              <Field
                label="Preaviso mínimo"
                type="number"
                value={booking.owner_stay_min_notice_days}
                onChange={(v) => set({ owner_stay_min_notice_days: v })}
                suffix="días"
                hint="Antelación con la que hay que pedirlas"
                approved={{ baseline: 30, value: booking.owner_stay_min_notice_days }}
                disabled={!booking.owner_stays_enabled}
                testId="booking-owner-notice"
              />
            </FieldGrid>
          </div>
        </Card>

        <Card className="mt-4" testId="booking-blackout-card">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <CalendarBlank size={14} className="text-stone-400" />
            <span className="text-[11px] tracking-[0.18em] uppercase text-stone-500 dark:text-stone-400">
              Periodos cerrados
            </span>
            {isApproved("owner_stay_blackout") && <ApprovedMark />}
          </div>
          <Note testId="booking-blackout-note">
            Se definen por periodo: cada uno cubre unas fechas concretas en las que no se aceptan
            estancias del propietario, normalmente porque la demanda es alta. Los periodos se
            editan en el calendario, no aquí.
          </Note>

          <div data-testid="booking-blackout-list">
            {blackout.length === 0 ? (
              <p className="text-sm text-stone-500 dark:text-stone-400" data-testid="booking-blackout-empty">
                Todavía no hay ningún periodo cerrado.
              </p>
            ) : (
              blackout.map((period, i) => (
                <ReadRow
                  key={period}
                  label={period}
                  value="Cerrado"
                  testId={`booking-blackout-row-${i}`}
                />
              ))
            )}
          </div>
        </Card>
      </Block>
    </div>
  );
}
